import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { RequesterCycleTime } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface CycleTimeByRequesterTableProps {
  data: RequesterCycleTime[];
  isLoading: boolean;
  isAdmin: boolean;
  currentUserId?: string;
}

type SortField = 'jobCount' | 'avgTotalCycleTime' | 'avgTimeToPublish' | 'avgTimeToAward' | 'avgProductionTime';
type SortDirection = 'asc' | 'desc';

const ROWS_PER_PAGE = 15;

export function CycleTimeByRequesterTable({ data, isLoading, isAdmin, currentUserId }: CycleTimeByRequesterTableProps) {
  const [sortField, setSortField] = useState<SortField>('jobCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(0);

  // Filter data based on role
  const filteredData = isAdmin ? data : data.filter(r => r.userId === currentUserId);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const sortedData = [...filteredData].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return (a[sortField] - b[sortField]) * multiplier;
  });

  const totalPages = Math.ceil(sortedData.length / ROWS_PER_PAGE);
  const paginatedData = sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const SortableHeader = ({ field, label, align = 'left' }: { field: SortField; label: string; align?: 'left' | 'right' }) => (
    <button
      onClick={() => handleSort(field)}
      className={cn(
        "flex items-center gap-1 hover:text-foreground transition-colors w-full",
        sortField === field && "text-foreground",
        align === 'right' && "justify-end"
      )}
    >
      {label}
      {sortField === field && (
        sortDirection === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
      )}
    </button>
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cycle Time by Requester</CardTitle>
          <CardDescription>Performance metrics by individual requesters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (filteredData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cycle Time by Requester</CardTitle>
          <CardDescription>Performance metrics by individual requesters</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No data available for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cycle Time by Requester</CardTitle>
        <CardDescription>
          {isAdmin 
            ? "Performance metrics by individual requesters" 
            : "Your cycle time performance"
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Requester</TableHead>
                <TableHead className="text-right">
                  <SortableHeader field="jobCount" label="Jobs" align="right" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader field="avgTotalCycleTime" label="Avg Total" align="right" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader field="avgTimeToPublish" label="Avg Publish" align="right" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader field="avgTimeToAward" label="Avg Award" align="right" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader field="avgProductionTime" label="Avg Production" align="right" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map(requester => (
                <TableRow key={requester.userId}>
                  <TableCell className="font-medium">{requester.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{requester.jobCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{requester.avgTotalCycleTime.toFixed(1)} days</TableCell>
                  <TableCell className="text-right tabular-nums">{requester.avgTimeToPublish.toFixed(1)} days</TableCell>
                  <TableCell className="text-right tabular-nums">{requester.avgTimeToAward.toFixed(1)} days</TableCell>
                  <TableCell className="text-right tabular-nums">{requester.avgProductionTime.toFixed(1)} days</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {page * ROWS_PER_PAGE + 1}-{Math.min((page + 1) * ROWS_PER_PAGE, sortedData.length)} of {sortedData.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
